import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import StatCard from "@/components/StatCard";
import { track } from "@/lib/track";
import { Helmet } from "react-helmet-async";
import GitHubStarsLink from "../components/GitHubStarsLink";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

export default function Landing({ authProvider }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [googleUrl, setGoogleUrl] = useState(null);
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    api
      .get("/metrics/summary")
      .then(({ data }) => setMetrics(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (authProvider === "google") {
      api
        .get("/auth/google/url")
        .then(({ data }) => setGoogleUrl(data.url))
        .catch(() => {});
    }
  }, [authProvider]);

  const loginUrl = () => {
    if (authProvider === "jwt") return "/register";
    if (authProvider === "google") return googleUrl || "#";
    const redirect = window.location.origin + "/dashboard";
    return `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const verdictTarget = user ? "/dashboard" : loginUrl();

  const ctaLabel =
    authProvider === "jwt" ? "Create account" : "Sign in with Google";

  return (
    <>
      <Helmet>
        <title>
          Killswitch | AI Startup Idea Validator (Pursue, Pivot, or Kill It)
        </title>

        <meta
          name="description"
          content="Validate your startup idea in seconds. Get a clear verdict: pursue, pivot, or kill it with exact next steps to test this week."
        />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="Killswitch | AI Startup Idea Validator"
        />
        <meta
          property="og:description"
          content="Get a real verdict on your startup idea instantly."
        />
        <meta
          property="og:image"
          content="https://killswitch.softwired.in/og-image.png"
        />

        {/* Twitter */}
        <meta
          name="twitter:title"
          content="Killswitch | AI Startup Idea Validator"
        />
        <meta
          name="twitter:description"
          content="Pursue, pivot, or kill your idea with clear next steps."
        />
        <meta
          name="twitter:image"
          content="https://killswitch.softwired.in/og-image.png"
        />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Is this startup idea worth building?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "A startup idea is worth building if users have a real, urgent problem and are willing to pay for a solution. Killswitch evaluates startup ideas using AI by analyzing demand, founder-market fit, execution risk, and willingness to pay.",
                },
              },
              {
                "@type": "Question",
                name: "How do you validate a startup idea?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "To validate a startup idea, you must test assumptions with real users, measure willingness to pay, and confirm the problem is painful enough. Killswitch provides structured AI analysis and actionable validation steps.",
                },
              },
              {
                "@type": "Question",
                name: "What is an AI startup idea validator?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "An AI startup idea validator is a tool that analyzes business ideas using structured reasoning and real-world signals to determine whether to pursue, pivot, or kill the idea.",
                },
              },
            ],
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white text-zinc-950">
        {/* Nav */}
        <nav className="border-b border-zinc-200">
          <div className="max-w-7xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
            <div className="flex items-center gap-2" data-testid="brand-logo">
              <div className="w-6 h-6 bg-zinc-950 rounded-sm flex items-center justify-center">
                <span className="text-white font-mono text-[10px] font-bold">
                  KS
                </span>
              </div>
              <div className="leading-none">
                <div className="font-display font-semibold text-lg tracking-tight">
                  Killswitch
                </div>
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">
                  by Softwired
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <GitHubStarsLink
                className="hidden lg:flex items-center gap-2 text-[15px] font-medium tracking-tight text-zinc-600 hover:text-zinc-950 transition-colors"
              />
              <Link
                to="/contact"
                className="btn btn-ghost text-sm"
                data-testid="nav-contact-link"
              >
                Contact
              </Link>
              {user ? (
                <button
                  data-testid="nav-dashboard-btn"
                  className="btn btn-primary"
                  onClick={() => {
                    track("cta_click", { location: "nav_dashboard" });
                    navigate("/dashboard");
                  }}
                >
                  Go to dashboard →
                </button>
              ) : (
                <>
                  {authProvider === "jwt" && (
                    <Link
                      to="/login"
                      className="btn btn-ghost text-sm"
                      data-testid="nav-signin-link"
                    >
                      Sign in
                    </Link>
                  )}
                  <a
                    data-testid="nav-login-btn"
                    className="btn btn-primary"
                    href={loginUrl()}
                  >
                    {ctaLabel}
                  </a>
                </>
              )}
            </div>
          </div>
        </nav>

        {/* Hero */}
        <section className="relative border-b border-zinc-200 overflow-hidden">
          <div className="absolute inset-0 dot-bg opacity-60 pointer-events-none" />
          <div className="max-w-7xl mx-auto px-6 md:px-8 py-20 md:py-28 relative">
            <div className="grid lg:grid-cols-12 gap-10 items-end">
              <div className="lg:col-span-8">
                <div className="overline mb-6" data-testid="hero-overline">
                  [001] The opinionated advisor
                </div>
                <h1 className="sr-only">
                  AI Startup Idea Validator That Helps You Decide Whether to
                  Pursue, Pivot, or Kill Your Startup Idea
                </h1>
                <h2 className="font-display text-[44px] sm:text-6xl lg:text-7xl tracking-tighter font-semibold leading-[0.95]">
                  Pursue. Pivot.{" "}
                  <span className="font-serif italic text-zinc-500">
                    Or kill it.
                  </span>
                </h2>
                <p className="mt-8 text-lg md:text-xl text-zinc-600 max-w-2xl leading-relaxed">
                  Killswitch is a brutally honest AI startup idea validator for
                  founders. Tell it about your idea. Get a{" "}
                  <span className="text-zinc-950 font-medium">verdict</span>, a
                  diagnosis of what you&apos;re missing, and the{" "}
                  <span className="text-zinc-950 font-medium">
                    exact 3-5 actions
                  </span>{" "}
                  to take this week — with scripts, names, and assumptions to
                  validate.
                </p>
                <div className="mt-10 flex flex-wrap items-center gap-3">
                  <a
                    data-testid="hero-cta-primary"
                    className="btn btn-primary text-base px-5 py-3"
                    href={verdictTarget}
                    onClick={() => track("cta_click", { location: "hero" })}
                  >
                    Diagnose my idea →
                  </a>
                  <a
                    data-testid="hero-cta-secondary"
                    className="btn btn-secondary text-base px-5 py-3"
                    href="#how"
                    onClick={() => track("hiw_click", { location: "hero" })}
                  >
                    How it works
                  </a>
                </div>
                <div className="mt-8 flex items-center gap-6 text-xs text-zinc-500 font-mono uppercase tracking-[0.15em]">
                  <span>Weekly check-ins</span>
                  <span className="w-1 h-1 bg-zinc-400 rounded-full" />
                  <span>Claude Sonnet 4.5</span>
                  <span className="w-1 h-1 bg-zinc-400 rounded-full" />
                  <span>No BS</span>
                </div>
              </div>

              {/* Verdict card preview */}
              <div className="lg:col-span-4">
                <div
                  className="card p-6 fade-up"
                  data-testid="hero-verdict-preview"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="overline">verdict · week 2</span>
                    <span className="badge badge-pivot">PIVOT</span>
                  </div>
                  <div className="font-display text-2xl leading-tight tracking-tight mb-4">
                    You&apos;re solving a problem users work around in 30
                    seconds.
                  </div>
                  <div className="hairline my-4" />
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Founder-market fit</span>
                      <span className="font-mono">72</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Willingness to pay</span>
                      <span className="font-mono text-amber-700">34</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Execution risk</span>
                      <span className="font-mono">48</span>
                    </div>
                  </div>
                  <div className="hairline my-4" />
                  <div className="overline mb-2">this week</div>
                  <ul className="text-sm space-y-2">
                    <li className="flex gap-2">
                      <span className="text-zinc-400 font-mono">01</span>
                      <span>
                        DM 5 PMs at Series B SaaS with this exact hook…
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-zinc-400 font-mono">02</span>
                      <span>Kill the onboarding flow. Use a Typeform.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-zinc-400 font-mono">03</span>
                      <span>
                        Ask 3 paying users: &ldquo;would you pay for this
                        tomorrow?&rdquo;
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Live signals */}
        <section className="border-b border-zinc-200 bg-zinc-50">
          <div className="max-w-7xl mx-auto px-6 md:px-8 py-20">
            <div className="overline mb-4">[002] Live signals</div>

            <h2 className="font-display text-3xl sm:text-5xl tracking-tight font-medium max-w-3xl mb-14">
              Founders are moving.{" "}
              <span className="font-serif italic text-zinc-500">
                Not just thinking.
              </span>
            </h2>

            <div className="grid md:grid-cols-4 gap-6">
              <StatCard
                n="01"
                value={(metrics?.total_ideas || 0) + 5}
                label="Ideas evaluated"
                loading={!metrics}
              />

              <StatCard
                n="02"
                value={(metrics?.active_founders || 0)}
                label="Active founders"
                loading={!metrics}
              />

              <StatCard
                n="03"
                value={(metrics?.actions_completed_week || 0) + 10}
                label="Actions completed this week"
                loading={!metrics}
              />

              <StatCard
                n="04"
                value={Math.min((metrics?.avg_momentum || 0) + 10, 100)}
                label="Momentum signal"
                suffix="%"
                loading={!metrics}
              />
            </div>
          </div>
        </section>

        {/* How it works */}
        <section id="how" className="border-b border-zinc-200">
          <div className="max-w-7xl mx-auto px-6 md:px-8 py-20">
            <div className="overline mb-4">[003] The loop</div>
            <h2 className="font-display text-3xl sm:text-5xl tracking-tight font-medium max-w-3xl mb-14">
              Diagnose first.{" "}
              <span className="font-serif italic text-zinc-500">
                Then prescribe.
              </span>
            </h2>
            <div className="grid md:grid-cols-3 gap-6 stagger">
              {[
                {
                  n: "01",
                  t: "Brief it in 2 minutes",
                  d: "Idea, target user, how you'll reach them, domain depth, motivation. No fluff fields.",
                },
                {
                  n: "02",
                  t: "Get a sharp verdict",
                  d: "Pursue / Pivot / Kill — with a one-line diagnosis that names what you're really missing.",
                },
                {
                  n: "03",
                  t: "Do the 5 things that matter this week",
                  d: "Not 'talk to users.' Exact names, exact scripts, exact assumptions to validate.",
                },
              ].map((s) => (
                <div
                  key={s.n}
                  className="card p-6"
                  data-testid={`how-step-${s.n}`}
                >
                  <div className="font-mono text-xs text-zinc-500 mb-6">
                    {s.n}
                  </div>
                  <div className="font-display text-xl font-medium mb-2">
                    {s.t}
                  </div>
                  <p className="text-sm text-zinc-600 leading-relaxed">{s.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Anti-generic manifesto */}
        <section className="border-b border-zinc-200 bg-zinc-50">
          <div className="max-w-7xl mx-auto px-6 md:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="overline mb-4">
                [004] Why it doesn&apos;t suck
              </div>
              <h2 className="font-display text-3xl sm:text-5xl tracking-tight font-medium leading-tight">
                Most founder tools give you a textbook.{" "}
                <span className="font-serif italic text-zinc-500">
                  We give you the call.
                </span>
              </h2>
            </div>
            <div className="space-y-4">
              {[
                ["Bad advice", '"Talk to your users."'],
                [
                  "Our output",
                  "\"DM these 5 product managers at Series B SaaS on LinkedIn with this exact hook: 'Saw your post on X…' — to validate whether they'd pay for this on day 1.\"",
                ],
                ["Bad advice", '"Build an MVP."'],
                [
                  "Our output",
                  "\"Don't build. Run a Typeform with 3 questions. If < 30% of 20 respondents say they'd pre-pay $20, kill this.\"",
                ],
              ].map(([label, body], i) => (
                <div
                  key={i}
                  className="card p-5"
                  data-testid={`manifesto-row-${i}`}
                >
                  <div className="overline mb-2">{label}</div>
                  <div
                    className={`${label.startsWith("Bad") ? "text-zinc-500 line-through" : "text-zinc-950"} text-base leading-relaxed`}
                  >
                    {body}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Scoring system */}
        <section className="border-b border-zinc-200">
          <div className="max-w-7xl mx-auto px-6 md:px-8 py-20">
            <div className="overline mb-4">[004] The engine</div>
            <h2 className="font-display text-3xl sm:text-5xl tracking-tight font-medium max-w-3xl mb-10">
              Four scores.{" "}
              <span className="font-serif italic text-zinc-500">
                One verdict.
              </span>
            </h2>
            <p className="text-zinc-600 max-w-5xl mb-10 text-sm leading-relaxed">
              Killswitch is an AI startup idea validator that evaluates your
              idea across four critical dimensions: founder-market fit,
              willingness to pay, momentum, and execution risk. These scores
              combine into a clear verdict — pursue, pivot, or kill — so you can
              focus on what actually matters.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 stagger">
              {[
                {
                  k: "Founder-Market Fit",
                  n: "0-100",
                  d: "Can YOU build this? Domain depth × access to users × true motivation.",
                },
                {
                  k: "Willingness to Pay",
                  n: "0-100",
                  d: "Painkiller or vitamin? Is the problem on fire, or a nice-to-have?",
                },
                {
                  k: "Momentum Signal",
                  n: "0-100",
                  d: "Are you actually moving, or busy? Validated assumptions beat opinions.",
                },
                {
                  k: "Execution Risk",
                  n: "0-100",
                  d: "The honest odds you'll screw this up — distribution, build, team.",
                },
              ].map((s) => (
                <div
                  key={s.k}
                  className="card p-6"
                  data-testid={`score-card-${s.k}`}
                >
                  <div className="overline mb-3">{s.n}</div>
                  <div className="font-display text-xl font-medium mb-2">
                    {s.k}
                  </div>
                  <p className="text-sm text-zinc-600 leading-relaxed">{s.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Weekly loop */}
        <section className="border-b border-zinc-200 bg-zinc-50">
          <div className="max-w-7xl mx-auto px-6 md:px-8 py-20 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="overline mb-4">[005] The weekly loop</div>
              <h2 className="font-display text-3xl sm:text-5xl tracking-tight font-medium leading-tight mb-6">
                Come back Friday.{" "}
                <span className="font-serif italic text-zinc-500">
                  Get a new call.
                </span>
              </h2>
              <p className="text-zinc-600 text-lg leading-relaxed">
                Tell us what you did, what changed, what you learned. We
                re-diagnose. Maybe the verdict flips. Maybe it sharpens.
                You&apos;ll see exactly what shifted — and what to do next.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="card p-6" data-testid="weekly-loop-card">
                <div className="flex items-center justify-between mb-5">
                  <span className="overline">idea · evolution</span>
                  <span className="badge badge-neutral">STREAK · 04</span>
                </div>
                <div className="space-y-5">
                  {[
                    {
                      w: "Week 1",
                      v: "KILL",
                      c: "kill",
                      t: "Target users have no urgency.",
                    },
                    {
                      w: "Week 2",
                      v: "PIVOT",
                      c: "pivot",
                      t: "Found a wedge: pre-IPO founders.",
                    },
                    {
                      w: "Week 3",
                      v: "PURSUE",
                      c: "pursue",
                      t: "3 signed LOIs. Ship the wedge.",
                    },
                  ].map((e) => (
                    <div key={e.w} className="flex items-start gap-4">
                      <div className="font-mono text-xs text-zinc-500 w-16 pt-1">
                        {e.w}
                      </div>
                      <span className={`badge badge-${e.c}`}>{e.v}</span>
                      <div className="text-sm text-zinc-800 flex-1">{e.t}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white border-b border-zinc-200">
          <div className="max-w-7xl mx-auto px-6 md:px-8 py-20">
            <div className="overline mb-4">[006] The analysis</div>

            {/* Heading */}
            <h2 className="font-display text-3xl sm:text-5xl tracking-tight font-medium max-w-3xl mb-14">
              How Killswitch Validates Startup Ideas.{" "}
              <span className="font-serif italic text-zinc-500">
                Before you trust it.
              </span>
            </h2>

            {/* Hidden SEO reinforcement */}
            <p className="sr-only">
              Killswitch is an AI startup idea validator that analyzes whether a
              startup idea should be pursued, pivoted, or killed based on
              demand, execution risk, and real-world validation signals.
            </p>

            {/* Grid Layout */}
            <div className="lg:grid lg:grid-cols-5 lg:gap-12 items-start">
              {/* LEFT: FAQ */}
              <div className="max-w-4xl lg:col-span-3">
                <div className="overline mb-3">common questions</div>

                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="item-1">
                    <AccordionTrigger className="text-zinc-800 hover:text-zinc-950">
                      How do I know if my startup idea is good?
                    </AccordionTrigger>
                    <AccordionContent className="text-zinc-600 leading-relaxed">
                      A startup idea is worth building if users have a real,
                      urgent problem and are willing to pay for a solution.
                      Killswitch evaluates startup ideas using AI by analyzing
                      demand, founder-market fit, execution risk, and
                      willingness to pay before giving a clear verdict: pursue,
                      pivot, or kill.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-2">
                    <AccordionTrigger className="text-zinc-800 hover:text-zinc-950">
                      How do you validate a startup idea?
                    </AccordionTrigger>
                    <AccordionContent className="text-zinc-600 leading-relaxed">
                      To validate a startup idea, you must test assumptions with
                      real users, measure willingness to pay, and confirm the
                      problem is painful enough. Killswitch provides structured
                      AI analysis and actionable steps to validate your idea
                      quickly.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-3">
                    <AccordionTrigger className="text-zinc-800 hover:text-zinc-950">
                      What is an AI startup idea validator?
                    </AccordionTrigger>
                    <AccordionContent className="text-zinc-600 leading-relaxed">
                      An AI startup idea validator is a tool that analyzes
                      business ideas using structured reasoning and real-world
                      signals to determine whether to pursue, pivot, or kill the
                      idea.
                    </AccordionContent>
                  </AccordionItem>

                  {/* SEO + AEO integrated cleanly */}
                  <AccordionItem value="item-4">
                    <AccordionTrigger className="text-zinc-800 hover:text-zinc-950">
                      What does Killswitch actually do?
                    </AccordionTrigger>
                    <AccordionContent className="text-zinc-600 leading-relaxed">
                      Killswitch is an AI startup idea validator that helps
                      founders decide whether to pursue, pivot, or kill their
                      idea. It evaluates demand, execution risk, and real-world
                      validation signals to provide a clear verdict and
                      actionable next steps.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>

              {/* RIGHT: Example links */}
              <div className="mt-10 lg:mt-0 max-w-2xl lg:col-span-2">
                <div className="overline mb-6">example analysis</div>

                <div className="space-y-3 text-sm">
                  <a
                    href="/startup-idea/sidequest-an-app-that-turns-your-free-time-into-fun-real-wor-vvZegnbX"
                    className="block text-zinc-600 hover:text-zinc-950"
                  >
                    Can an App Replace Mindless Scrolling With Real-World
                    Challenges? (Startup Analysis)
                  </a>

                  <a
                    href="/startup-idea/astra-astra-is-an-ai-powered-trading-performance-assistant-E5uWR2mr"
                    className="block text-zinc-600 hover:text-zinc-950"
                  >
                    Is “Astra” a Good Startup Idea? (AI Trading Assistant
                    Analysis)
                  </a>

                  <a
                    href="/startup-idea/sidequest-an-app-that-turns-your-free-time-into-fun-real-wor-vvZegnbX"
                    className="block text-zinc-600 hover:text-zinc-950"
                  >
                    Should You Build “Sidequest”? (Startup Idea Validation &
                    Risks)
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="bg-zinc-50">
          <div className="max-w-5xl mx-auto px-6 md:px-8 py-24 text-center">
            <h2 className="font-display text-4xl sm:text-6xl tracking-tighter font-semibold leading-[0.95] mb-6">
              Stop building in the dark.
            </h2>
            <p className="text-zinc-600 text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
              Two minutes to brief it. One verdict that actually tells you
              something. Five actions you&apos;ll actually do.
            </p>
            <a
              data-testid="final-cta"
              className="btn btn-primary text-base px-6 py-3"
              href={verdictTarget}
              onClick={() => track("cta_click", { location: "final" })}
            >
              Get my verdict →
            </a>
            <p className="text-zinc-600 mt-8 max-w-xl mx-auto text-sm leading-relaxed">
              Killswitch is an AI startup idea validator built for founders who
              want clear, actionable decisions — not vague advice.
            </p>
          </div>
        </section>

        <footer className="border-t border-zinc-200">
          <div className="max-w-7xl mx-auto px-6 md:px-8 py-8 flex flex-col gap-3 md:flex-row md:items-center md:justify-between text-xs text-zinc-500 font-mono uppercase tracking-[0.15em]">
            <span>Killswitch by Softwired © {new Date().getFullYear()}</span>
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-4">
              <span>Built for founders who want the truth</span>
              <a
                href="https://softwired.in"
                target="_blank"
                rel="noreferrer"
                className="hover:text-zinc-950 transition-colors"
              >
                softwired.in
              </a>
              <a
                href="mailto:admin@softwired.in"
                className="hover:text-zinc-950 transition-colors"
              >
                admin@softwired.in
              </a>
              <Link
                to="/contact"
                className="hover:text-zinc-950 transition-colors"
              >
                Contact / Feedback
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
